"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Box, IconButton, Typography, Fade, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { alpha, styled, keyframes } from "@mui/material/styles";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PersonIcon from "@mui/icons-material/Person";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import ProfileCardSkeleton from "./components/ProfileCardSkeleton";
import CandidateListSkeleton from "./components/CandidateListSkeleton";
import SearchContainer from "./components/SearchContainer";
import SearchResults from "./components/SearchResults";
import SearchDropdown from "./components/SearchDropdown";
import { useSearch } from "./hooks/useSearch";
import { useRecentSearches } from "./hooks/useRecentSearches";
import { useVoiceSearch } from "../support/components/hooks/VoiceSearchHook";
import { useSession } from "next-auth/react";
import { VALIDATION_MESSAGES } from "./constants/searchConfig";
import { useSearchParams, useRouter } from "next/navigation";

import {
  isValidName,
  isValidEmail,
  isValidLinkedIn,
} from "./utils/searchValidators";
import { track } from "@/lib/analytics/track";
import { EVENTS } from "@/lib/analytics/events";

const FullScreenContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  position: "relative",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  overflowX: "visible",
  overflowY: "visible",
  background: "transparent",
  margin: 0,
  padding: 0,
  [theme.breakpoints.down("md")]: {
    padding: 0,
    // The decorative halo extends past the input, but must not widen the page.
    overflowX: "clip",
    justifyContent: "center",
    height: "100%",
    paddingLeft: "env(safe-area-inset-left)",
    paddingRight: "env(safe-area-inset-right)",
    paddingBottom: "env(safe-area-inset-bottom)",
  },
  "@media (display-mode: standalone)": {
    [theme.breakpoints.down("md")]: {
      paddingLeft: "env(safe-area-inset-left)",
      paddingRight: "env(safe-area-inset-right)",
      paddingBottom: "env(safe-area-inset-bottom)",
    },
  },
  "@supports (-webkit-touch-callout: none)": {
    [theme.breakpoints.down("md")]: {
      paddingLeft: "env(safe-area-inset-left)",
      paddingRight: "env(safe-area-inset-right)",
      paddingBottom: "env(safe-area-inset-bottom)",
    },
  },
}));

const SearchHalo = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  maxWidth: "700px",
  minHeight: "60px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "visible",
  boxSizing: "border-box",
  zIndex: 0,
  isolation: "isolate",
  "&::before": {
    content: '""',
    position: "absolute",
    left: "50%",
    top: "50%",
    width: "205%",
    maxWidth: "1480px",
    height: "520px",
    borderRadius: "999px",
    background: `radial-gradient(ellipse at center, ${alpha(
      theme.palette.secondary.main,
      0.32,
    )} 0%, ${alpha(theme.palette.secondary.main, 0.18)} 44%, ${alpha(
      theme.palette.secondary.main,
      0.08,
    )} 62%, ${alpha(theme.palette.secondary.main, 0)} 80%)`,
    filter: "blur(40px)",
    transform: "translate(-50%, -28%)",
    pointerEvents: "none",
    zIndex: -1,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    left: "50%",
    top: "50%",
    width: "175%",
    maxWidth: "1220px",
    height: "335px",
    borderRadius: "999px",
    background: `radial-gradient(ellipse at center, ${alpha(
      theme.palette.secondary.main,
      0.26,
    )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 46%, ${alpha(
      theme.palette.secondary.main,
      0,
    )} 82%)`,
    filter: "blur(24px)",
    transform: "translate(-50%, -38%)",
    pointerEvents: "none",
    zIndex: -1,
  },
  "@media (max-width: 1024px), (max-height: 768px)": {
    "&::before": {
      width: "52%",
      height: "56px",
      filter: "blur(2px)",
      transform: "translate(-50%, -54%)",
    },
    "&::after": {
      display: "none",
    },
  },
  [theme.breakpoints.down("xl")]: {
    "&::before": {
      width: "165%",
      height: "360px",
      filter: "blur(26px)",
      transform: "translate(-50%, -35%)",
    },
    "&::after": {
      width: "140%",
      height: "220px",
      filter: "blur(16px)",
      transform: "translate(-50%, -47%)",
    },
  },
  [theme.breakpoints.down("md")]: {
    "&::before": {
      width: "145%",
      height: "300px",
      filter: "blur(20px)",
      transform: "translate(-50%, -38%)",
    },
    "&::after": {
      width: "120%",
      height: "180px",
      filter: "blur(12px)",
      transform: "translate(-50%, -50%)",
    },
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    maxWidth: "100%",
    "&::before": {
      width: "calc(100% + 48px)",
      maxWidth: "calc(100% + 48px)",
      height: "180px",
      filter: "blur(12px)",
      transform: "translate(-50%, -44%)",
    },
    "&::after": {
      width: "calc(100% + 24px)",
      maxWidth: "calc(100% + 24px)",
      height: "110px",
      filter: "blur(8px)",
      transform: "translate(-50%, -56%)",
    },
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "700px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    gap: theme.spacing(2),
    padding: 0,
  },
}));

const waveBounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.45;
  }
  40% {
    transform: translateY(-7px);
    opacity: 1;
  }
`;

const SearchWaveLoader = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "flex-end",
  justifyContent: "center",
  gap: 8,
  marginTop: theme.spacing(1),
  minHeight: 12,
  "& span": {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor:
      theme.palette.mode === "light"
        ? theme.palette.grey[500]
        : theme.palette.grey[200],
    animation: `${waveBounce} 900ms ease-in-out infinite`,
    transformOrigin: "center bottom",
  },
  "& span:nth-of-type(2)": {
    animationDelay: "120ms",
  },
  "& span:nth-of-type(3)": {
    animationDelay: "240ms",
  },
}));

const voiceBlobMorph = keyframes`
  0% {
    transform: scaleY(0.86);
    opacity: 0.85;
  }
  38% {
    transform: scaleY(1.14);
    opacity: 1;
  }
  100% {
    transform: scaleY(0.86);
    opacity: 0.85;
  }
`;

const voiceBlobGentleWave = keyframes`
  0%, 100% {
    transform: scaleY(0.97);
    opacity: 0.93;
  }
  50% {
    transform: scaleY(1.03);
    opacity: 0.98;
  }
`;

const voiceTypewriterCaret = keyframes`
  0%, 49% {
    opacity: 1;
  }
  50%, 100% {
    opacity: 0;
  }
`;

const SearchModeLayer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "instant",
})(({ active, instant }) => ({
  width: "100%",
  position: "relative",
  zIndex: 2,
  transition: instant
    ? "none"
    : "opacity 260ms ease, transform 300ms ease, filter 260ms ease",
  opacity: active ? 1 : 0,
  transform: active ? "scale(1) translateY(0)" : "scale(0.97) translateY(8px)",
  filter: active ? "blur(0px)" : "blur(2px)",
  pointerEvents: active ? "auto" : "none",
}));

const VoiceSessionPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "instant",
})(({ theme, active, instant }) => ({
  width: "100%",
  maxWidth: "700px",
  minHeight: 250,
  borderRadius: 34,
  padding: theme.spacing(1.6, 2, 1.4),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.35),
  position: "absolute",
  left: "50%",
  top: "calc(50% + 22px)",
  transform: active
    ? "translate(-50%, -50%) scale(1)"
    : "translate(-50%, -50%) scale(0.96)",
  zIndex: 3,
  overflow: "hidden",
  background: "transparent",
  border: "none",
  boxShadow: "none",
  opacity: active ? 1 : 0,
  filter: active ? "blur(0px)" : "blur(3px)",
  pointerEvents: active ? "auto" : "none",
  transition: instant
    ? "none"
    : "opacity 280ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1), filter 280ms ease",
  [theme.breakpoints.down("sm")]: {
    top: "calc(50% + 42px)",
    minHeight: 220,
    padding: theme.spacing(1.3, 1.1, 1.2),
    borderRadius: 24,
  },
}));

const VoiceBlobRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  minHeight: 88,
  marginTop: 0,
}));

const VoiceBlob = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "stage" &&
    prop !== "level" &&
    prop !== "isListening" &&
    prop !== "isSpeaking",
})(({ stage, level = 0.12, isListening, isSpeaking, theme }) => ({
  width: 50,
  height: 50,
  borderRadius: 18,
  backgroundColor: isListening ? "#ffffff" : theme.palette.primary.main,
  border: isListening
    ? "1px solid rgba(15, 23, 42, 0.16)"
    : `1px solid ${alpha(theme.palette.primary.dark, 0.42)}`,
  boxShadow: isListening
    ? "0 3px 10px rgba(15, 23, 42, 0.08)"
    : `0 8px 20px ${alpha(theme.palette.primary.main, 0.34)}`,
  transformOrigin: "center bottom",
  transform:
    isListening && isSpeaking
      ? `scaleY(${Math.max(0.9, Math.min(1.28, 0.9 + level * 0.42))})`
      : "scaleY(1)",
  transition: "transform 90ms linear, opacity 120ms ease",
  opacity: isListening ? 0.98 : 0.92,
  animation: isListening
    ? isSpeaking
      ? "none"
      : `${voiceBlobGentleWave} 1220ms ease-in-out infinite`
    : `${voiceBlobMorph} ${
        stage === "processing" ? "1100ms" : "980ms"
      } ease-in-out infinite`,
  display: "flex",
  "&:nth-of-type(2)": {
    height: 56,
    animationDelay: "110ms",
  },
  "&:nth-of-type(3)": {
    height: 60,
    animationDelay: "190ms",
  },
  "&:nth-of-type(4)": {
    height: 50,
    animationDelay: "300ms",
  },
}));

const VOICE_MAX_DURATION_SECONDS = 30;
const VOICE_LOADING_STATUS = [
  "Listening...",
  "Analyzing...",
  "Processing...",
  "Searching...",
];
const CONFIRM_YES_RE = /\b(?:yes|yeah|yep|yup|ya|yup yup|yep yep|yas|yea|correct|right|sure|go ahead|do it|please do|confirm|exactly|ok|okay|okey|kay)\b/i;
const CONFIRM_NO_RE = /\b(?:no|nope|nah|know|no no|wrong|not now|stop|cancel|don't|do not|dont)\b/i;
const VOICE_CANCEL_FLOW_RE = /\b(?:cancel|stop|exit|quit|go back|back to search|back to the search|return to search|close voice|end voice|never mind|nevermind|nothing,? just back|goodbye|good bye|bye bye|that's all|thats all|no thanks|thank you,? bye|thank you,? that's all|thank you,? thats all)\b/i;
const VOICE_CONFIRM_PROMPTS = [
  "${recap} Is that correct?",
  "${recap} Did I get that right?",
  "${recap} Does that look right to you?",
  "${recap} Should I use this and search now?",
  "${recap} Want me to go ahead with this?",
];
const VOICE_CONFIRM_HINT_PROMPTS = [
  "If that is right, say yes. If not, say no.",
  "If this sounds right, say yes. If not, say no.",
  "If I got it right, say yes. Otherwise, say no.",
  "Say yes to continue, or no and I will listen again.",
];
const VOICE_RETRY_AFTER_NO_PROMPTS = [
  "No problem. Say it again and I will confirm before searching.",
  "Sure. Tell me again, and I will double-check it first.",
  "Got it. Please restate it, then I will confirm once more.",
  "Okay, let us try again. Say the request one more time.",
];
const VOICE_PENDING_RETRY_PROMPTS = [
  "I lost that request. Please tell me who you want to meet.",
  "I do not have it yet. Please say who you want to meet.",
  "I missed the request. Tell me again who you want to meet.",
];
const VOICE_SEARCHING_CONFIRMATIONS = [
  "Great. Searching now.",
  "Perfect. I am searching now.",
  "Nice, starting the search now.",
  "Got it. Searching right away.",
];

const normalizeShortConfirmation = (text) => {
  const cleaned = String(text || "")
    .toLowerCase()
    .replace(/[^a-z\s']/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return "";

  if (
    /^(?:y|ye|yes|yeah|yep|yup|ya|yas|yea|sure|ok|okay|okey|correct|right|go ahead|do it|confirm|please do)$/i.test(
      cleaned,
    )
  ) {
    return "yes";
  }

  if (
    /^(?:n|no|noo|nope|nah|stop|cancel|wrong|do not|don't|dont|not now)$/i.test(
      cleaned,
    )
  ) {
    return "no";
  }

  return "";
};

const isCancelVoiceIntent = (text) => {
  const raw = String(text || "")
    .trim()
    .toLowerCase();
  if (!raw) return false;
  if (VOICE_CANCEL_FLOW_RE.test(raw)) return true;
  return /^(?:cancel|stop|exit|quit|go back|never mind|nevermind|goodbye|good bye|bye|bye bye|that's all|thats all|no thanks|thank you,? bye|thank you,? that's all|thank you,? thats all)[.!\s]*$/i.test(
    raw,
  );
};

const inferConfirmationFromNluPayload = (parsed) => {
  const tokens = [
    parsed?.normalizedQuery,
    parsed?.summary,
    parsed?.clarificationPrompt,
    parsed?.searchType,
    parsed?.intent,
  ]
    .map((v) => String(v || "").toLowerCase())
    .join(" ");

  if (!tokens) return "";

  if (
    /(?:\byes\b|\bok\b|\bokay\b|\bconfirm\b|\bgo ahead\b|\bcorrect\b|\bright\b)/i.test(
      tokens,
    )
  ) {
    return "yes";
  }

  if (
    /(?:\bno\b|\bnope\b|\bnah\b|\bcancel\b|\bstop\b|\bwrong\b)/i.test(tokens)
  ) {
    return "no";
  }

  return "";
};

const pickRandom = (items, fallback = "") => {
  if (!Array.isArray(items) || items.length === 0) return fallback;
  const index = Math.floor(Math.random() * items.length);
  return items[index] || fallback;
};

const toHumanSearchType = (type) => {
  const key = String(type || "").toLowerCase();
  if (key === "email") return "email";
  if (key === "linkedin") return "LinkedIn profile";
  if (key === "company") return "company";
  if (key === "name") return "name";
  if (key === "role") return "role";
  if (key === "location") return "location";
  if (key === "discover") return "request";
  return "request";
};

const inferHumanDetailLabel = (query, inferredType) => {
  const q = String(query || "").trim();
  const typeLabel = toHumanSearchType(inferredType);

  if (!q) return typeLabel;
  if (/@/.test(q)) return "email";
  if (/linkedin\.com|\bin\//i.test(q)) return "LinkedIn profile";
  if (/\b(at|@)\b/i.test(q)) return "name and company";
  if (/\b(in|from|near|based in)\b/i.test(q)) return "role or location";
  return typeLabel;
};

const buildVoiceRecap = (normalizedQuery, inferredType, summary) => {
  const query = String(normalizedQuery || "").trim();
  const typeLabel = inferHumanDetailLabel(query, inferredType);
  const safeSummary = String(summary || "").trim();

  if (query) {
    if (typeLabel === "request") {
      return `I heard: "${query}".`;
    }
    return `I heard ${typeLabel}: "${query}".`;
  }

  if (safeSummary) {
    return safeSummary.endsWith(".") ? safeSummary : `${safeSummary}.`;
  }

  return "I heard your request.";
};

const buildConfirmPrompt = (summary, normalizedQuery, inferredType) => {
  const recap = buildVoiceRecap(normalizedQuery, inferredType, summary);
  const template = pickRandom(
    VOICE_CONFIRM_PROMPTS,
    "${recap} Is that correct?",
  );
  const body = template.replace("${recap}", recap);
  const hint = pickRandom(
    VOICE_CONFIRM_HINT_PROMPTS,
    "Say yes to search, or no to change it.",
  );
  return `${body} ${hint}`;
};

const formatVoiceTime = (seconds) => {
  const safe = Math.max(0, Number(seconds) || 0);
  const mins = String(Math.floor(safe / 60)).padStart(2, "0");
  const secs = String(safe % 60).padStart(2, "0");
  return `${mins}:${secs}`;
};

export default function SearchPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: session, status: authStatus } = useSession();
  const isGuestUser = authStatus !== "authenticated";
  const searchParams = useSearchParams();
  const router = useRouter();

  const {
    searchQuery,
    setSearchQuery,
    searchType,
    setSearchType,
    isLoading,
    showSkeleton,
    skeletonMode,
    tableSkeletonReady,
    results,
    moreResults,
    rawCandidates,
    showTableNarrowHint,
    rcTotalCount,
    rcHasMore,
    externalLimitReached,
    savedHasMore,
    isLoadingMoreRC,
    enrichingId,
    enrichedUrls,
    showResults,
    suggestions,
    suggestionLabel,
    isNameOnlySearch,
    inputRef,
    detectSearchType,
    getSmartSuggestion,
    isValidEmail: hookIsValidEmail,
    isValidLinkedIn: hookIsValidLinkedIn,
    searchError,
    externalSearchUnavailable,
    searchCooldown,
    retryInitialSearch,
    setSearchError,
    handleSearch,
    resetSearch,
    refineSearch,
    handleShowMore,
    handleLoadNextRCPage,
    handleEnrichOne,
    isSearchValid,
    nameQuery,
    companyQuery,
  } = useSearch();

  // ── Feature: recent searches ──────────────────────────────────────────────
  const {
    recentSearches,
    save: saveRecent,
    remove: removeRecent,
    clear: clearRecent,
  } = useRecentSearches();
  const [inputFocused, setInputFocused] = useState(false);

  const {
    isListening,
    isTranscribing,
    speechSupported,
    voiceError: voiceSessionError,
    setupVoiceRecognition,
    startVoiceCapture,
    stopVoiceCapture,
    cancelVoiceSession,
  } = useVoiceSearch();

  const [voiceStage, setVoiceStage] = useState("idle");
  const [voiceSecondsLeft, setVoiceSecondsLeft] = useState(
    VOICE_MAX_DURATION_SECONDS,
  );
  const [voiceError, setVoiceError] = useState("");
  const [voiceStatusIndex, setVoiceStatusIndex] = useState(0);
  const [voiceInstantSwitch, setVoiceInstantSwitch] = useState(false);
  const [voiceChatLines, setVoiceChatLines] = useState([]);
  const [voiceTypedLineId, setVoiceTypedLineId] = useState(null);
  const [voiceTypedText, setVoiceTypedText] = useState("");
  const [voiceLevels, setVoiceLevels] = useState([0.11, 0.14, 0.13, 0.1]);
  const voiceSessionRef = useRef(0);
  const voiceTranscriptRef = useRef("");
  const voiceHasStartedRef = useRef(false);
  const voiceRelistenTimerRef = useRef(null);
  const voiceListenHealthTimerRef = useRef(null);
  const voiceListenHealthRetryRef = useRef(0);
  const voiceLowConfidenceRetryRef = useRef(0);
  const voiceAwaitingConfirmRef = useRef(false);
  const voicePendingIntentRef = useRef(null);
  const voiceStageRef = useRef("idle");
  const voiceMeterTickRef = useRef(null);
  const voiceMeterStreamRef = useRef(null);
  const voiceMeterContextRef = useRef(null);
  const voiceMeterAnalyserRef = useRef(null);
  const voiceMeterSourceRef = useRef(null);
  const voiceChatLineIdRef = useRef(0);
  const voiceChatLastSignatureRef = useRef("");
  const voiceLastAssistantReplyRef = useRef("");
  const voicePendingUserLineIdRef = useRef(null);
  const voiceLiveTranscriptRef = useRef("");
  const voiceTypewriterTimerRef = useRef(null);
  const voiceInstantTimerRef = useRef(null);
  const voiceUnderstandingAbortRef = useRef(null);

  // ── Feature: keyboard navigation in results ───────────────────────────────
  const [activeCardIndex, setActiveCardIndex] = useState(-1);
  const cardProfileIds = results.map((r) => r.url || r.id).filter(Boolean);

  // Global arrow-key listener — active only while the results view is shown
  // (the search input is not mounted then, so handleKeyDown on the input
  // never fires in that state).
  useEffect(() => {
    if (!showResults) return;
    const onKey = (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        setActiveCardIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        setActiveCardIndex((prev) => Math.max(prev - 1, 0));
      } else if (
        e.key === "Enter" &&
        activeCardIndex >= 0 &&
        cardProfileIds[activeCardIndex]
      ) {
        e.preventDefault();
        router.push(`/profile/${cardProfileIds[activeCardIndex]}`);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showResults, results.length, activeCardIndex, cardProfileIds, router]);

  const [showContent, setShowContent] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const isVoiceModeActive =
    voiceStage === "listening" ||
    voiceStage === "processing" ||
    voiceStage === "speaking" ||
    voiceStage === "searching";

  useEffect(() => {
    voiceStageRef.current = voiceStage;
  }, [voiceStage]);
  const voiceStatusLabel =
    voiceStage === "listening"
      ? isTranscribing
        ? "Finishing transcription..."
        : "Listening..."
      : voiceStage === "processing"
      ? "On it..."
      : voiceStage === "speaking"
      ? "Typing..."
      : voiceStage === "searching"
      ? "Finding the best matches..."
      : "Tap mic to talk";

  const capitalizeNames = (text) => {
    return text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  useEffect(() => {
    setShowContent(true);
    setHasStarted(true);

    const urlSearchQuery = searchParams.get("search");
    if (urlSearchQuery) {
      const decodedQuery = decodeURIComponent(urlSearchQuery);
      const detectedType = detectSearchType(decodedQuery);
      const processedQuery =
        detectedType === "name" ? capitalizeNames(decodedQuery) : decodedQuery;
      setSearchQuery(processedQuery);
      setSearchType(detectedType);
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 100);
    }
  }, [searchParams, setSearchQuery, setSearchType, detectSearchType, inputRef]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (searchError) setSearchError("");
    setSearchQuery(value);
    setSearchType(detectSearchType(value));
  };

  const handleVoiceSearchStart = useCallback(() => {
    voiceHasStartedRef.current = true;
    setInputFocused(true);
    setVoiceStage("listening");
    setVoiceError("");
    setVoiceSecondsLeft(VOICE_MAX_DURATION_SECONDS);
  }, []);

  // Wrap handleSearch to save the query as a recent search
  const handleSearchWithRecent = useCallback(
    (queryOverride = null) => {
      if (isLoading) return;

      const q =
        typeof queryOverride === "string"
          ? queryOverride.trim()
          : searchQuery.trim();
      if (q) saveRecent(q);
      setActiveCardIndex(-1);
      if (q) {
        track(EVENTS.SEARCH_PERFORMED, {
          query: q,
          query_length: q.length,
        });
      }
      handleSearch(q);
    },
    [searchQuery, saveRecent, handleSearch, isLoading],
  );

  const stopVoiceMeter = useCallback(() => {
    if (voiceMeterTickRef.current) {
      clearTimeout(voiceMeterTickRef.current);
      voiceMeterTickRef.current = null;
    }

    if (voiceMeterSourceRef.current) {
      try {
        voiceMeterSourceRef.current.disconnect();
      } catch {}
      voiceMeterSourceRef.current = null;
    }

    if (voiceMeterAnalyserRef.current) {
      try {
        voiceMeterAnalyserRef.current.disconnect();
      } catch {}
      voiceMeterAnalyserRef.current = null;
    }

    if (voiceMeterStreamRef.current) {
      voiceMeterStreamRef.current.getTracks().forEach((track) => track.stop());
      voiceMeterStreamRef.current = null;
    }

    if (voiceMeterContextRef.current) {
      try {
        voiceMeterContextRef.current.close();
      } catch {}
      voiceMeterContextRef.current = null;
    }

    setVoiceLevels([0.11, 0.14, 0.13, 0.1]);
  }, []);

  const appendVoiceChatLine = useCallback((speaker, text, options = {}) => {
    const cleanText = String(text || "").trim();
    if (!cleanText) return;

    const signature = `${speaker}:${cleanText.toLowerCase()}`;
    if (voiceChatLastSignatureRef.current === signature) return;
    voiceChatLastSignatureRef.current = signature;

    const nextId = voiceChatLineIdRef.current + 1;
    voiceChatLineIdRef.current = nextId;

    setVoiceChatLines((prev) => {
      const next = [...prev, { id: nextId, speaker, text: cleanText }];
      return next.slice(-4);
    });

    setVoiceTypedLineId(nextId);
    setVoiceTypedText("");

    if (voiceTypewriterTimerRef.current) {
      clearInterval(voiceTypewriterTimerRef.current);
      voiceTypewriterTimerRef.current = null;
    }

    let index = 0;
    const providedDelay = Number(options?.tickDelayMs);
    const defaultDelay =
      speaker === "ai"
        ? cleanText.length > 120
          ? 34
          : cleanText.length > 70
          ? 38
          : 44
        : cleanText.length > 120
        ? 9
        : cleanText.length > 70
        ? 12
        : 16;
    const tickDelay = Number.isFinite(providedDelay)
      ? Math.max(10, Math.min(90, Math.floor(providedDelay)))
      : defaultDelay;
    voiceTypewriterTimerRef.current = setInterval(() => {
      index += 1;
      setVoiceTypedText(cleanText.slice(0, index));
      if (index >= cleanText.length) {
        if (voiceTypewriterTimerRef.current) {
          clearInterval(voiceTypewriterTimerRef.current);
          voiceTypewriterTimerRef.current = null;
        }
      }
    }, tickDelay);
  }, []);

  const ensureVoiceUserListeningLine = useCallback(() => {
    if (voiceTypewriterTimerRef.current) {
      clearInterval(voiceTypewriterTimerRef.current);
      voiceTypewriterTimerRef.current = null;
    }
    const existingId = voicePendingUserLineIdRef.current;
    if (existingId) {
      setVoiceTypedLineId(existingId);
      setVoiceTypedText(voiceLiveTranscriptRef.current);
      return existingId;
    }

    const nextId = voiceChatLineIdRef.current + 1;
    voiceChatLineIdRef.current = nextId;
    voicePendingUserLineIdRef.current = nextId;

    setVoiceChatLines((prev) => {
      const next = [...prev, { id: nextId, speaker: "user", text: "" }];
      return next.slice(-4);
    });
    setVoiceTypedLineId(nextId);
    setVoiceTypedText("");
    return nextId;
  }, []);

  const clearPendingVoiceUserLine = useCallback(() => {
    const pendingId = voicePendingUserLineIdRef.current;
    voiceLiveTranscriptRef.current = "";
    if (!pendingId) return;

    setVoiceChatLines((prev) => prev.filter((line) => line.id !== pendingId));
    setVoiceTypedLineId((current) => (current === pendingId ? null : current));
    setVoiceTypedText("");
    voicePendingUserLineIdRef.current = null;
  }, []);

  const commitVoiceUserLine = useCallback(
    (transcript) => {
      const cleanText = String(transcript || "").trim();
      if (!cleanText) return;

      voiceLiveTranscriptRef.current = "";

      const pendingId = voicePendingUserLineIdRef.current;
      if (!pendingId) {
        appendVoiceChatLine("user", cleanText);
        return;
      }

      setVoiceChatLines((prev) => {
        const exists = prev.some((line) => line.id === pendingId);
        const next = prev.map((line) => {
          if (line.id === pendingId) {
            return { ...line, text: cleanText };
          }
          return line;
        });
        return (
          exists
            ? next
            : [...next, { id: pendingId, speaker: "user", text: cleanText }]
        ).slice(-4);
      });

      voicePendingUserLineIdRef.current = null;
      voiceChatLastSignatureRef.current = `user:${cleanText.toLowerCase()}`;

      if (voiceTypewriterTimerRef.current) {
        clearInterval(voiceTypewriterTimerRef.current);
        voiceTypewriterTimerRef.current = null;
      }

      setVoiceTypedLineId(null);
      setVoiceTypedText("");
    },
    [appendVoiceChatLine],
  );

  const triggerInstantSearchReturn = useCallback(() => {
    voiceSessionRef.current += 1;
    voiceStageRef.current = "idle";
    voiceAwaitingConfirmRef.current = false;
    voicePendingIntentRef.current = null;
    cancelVoiceSession();
    stopVoiceMeter();
    if (voiceInstantTimerRef.current) {
      clearTimeout(voiceInstantTimerRef.current);
      voiceInstantTimerRef.current = null;
    }

    setVoiceInstantSwitch(true);
    setVoiceStage("idle");
    setVoiceError("");
    voiceHasStartedRef.current = false;
    clearPendingVoiceUserLine();

    voiceInstantTimerRef.current = setTimeout(() => {
      setVoiceInstantSwitch(false);
      voiceInstantTimerRef.current = null;
    }, 180);
  }, [cancelVoiceSession, clearPendingVoiceUserLine, stopVoiceMeter]);

  useEffect(() => {
    return () => {
      if (voiceTypewriterTimerRef.current) {
        clearInterval(voiceTypewriterTimerRef.current);
        voiceTypewriterTimerRef.current = null;
      }
    };
  }, []);

  const showVoiceReply = useCallback(
    (text) => {
      const cleanText = String(text || "").trim();
      if (!cleanText) return;
      voiceLastAssistantReplyRef.current = cleanText
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();
      appendVoiceChatLine("ai", cleanText);
    },
    [appendVoiceChatLine],
  );

  const startVoiceListening = useCallback(
    (sessionId) => {
      if (voiceSessionRef.current !== sessionId) return;
      // Reset per-turn state to avoid stale values keeping the UI in listening.
      voiceTranscriptRef.current = "";
      voiceHasStartedRef.current = false;
      voiceListenHealthRetryRef.current = 0;
      setVoiceError("");
      setVoiceSecondsLeft(VOICE_MAX_DURATION_SECONDS);

      if (voiceRelistenTimerRef.current) {
        clearTimeout(voiceRelistenTimerRef.current);
      }

      if (voiceListenHealthTimerRef.current) {
        clearTimeout(voiceListenHealthTimerRef.current);
        voiceListenHealthTimerRef.current = null;
      }

      voiceRelistenTimerRef.current = setTimeout(async () => {
        if (voiceSessionRef.current !== sessionId) return;
        const followUpConfirmMode = voiceAwaitingConfirmRef.current;
        const started = await startVoiceCapture(
          () => {
            handleVoiceSearchStart();
          },
          {
            continuous: followUpConfirmMode,
            maxListenMs: followUpConfirmMode ? 2400 : 0,
            emitOnEndOnly: followUpConfirmMode,
          },
        );
        if (!started && voiceSessionRef.current === sessionId) {
          setVoiceError(
            "Could not start voice search. Check microphone access and try again.",
          );
          setVoiceStage("idle");
        }
      }, 140);
    },
    [handleVoiceSearchStart, startVoiceCapture],
  );

  const analyzeVoiceQuery = useCallback(async (transcript) => {
    voiceUnderstandingAbortRef.current?.abort();
    const controller = new AbortController();
    voiceUnderstandingAbortRef.current = controller;
    const response = await fetch("/api/search/voice-understanding", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transcript }),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error("Voice understanding failed");
    }

    return response.json();
  }, []);

  const processVoiceTranscript = useCallback(
    async (transcript, sessionId) => {
      const rawText = String(transcript || "").trim();
      if (!rawText) {
        if (voiceSessionRef.current !== sessionId) return;
        triggerInstantSearchReturn();
        return;
      }

      if (voiceSessionRef.current !== sessionId) return;

      if (isCancelVoiceIntent(rawText)) {
        voiceAwaitingConfirmRef.current = false;
        voicePendingIntentRef.current = null;
        triggerInstantSearchReturn();
        return;
      }

      if (voiceAwaitingConfirmRef.current) {
        let resolvedConfirm = normalizeShortConfirmation(rawText);
        if (!resolvedConfirm) {
          if (CONFIRM_YES_RE.test(rawText)) {
            resolvedConfirm = "yes";
          } else if (CONFIRM_NO_RE.test(rawText)) {
            resolvedConfirm = "no";
          }
        }

        if (!resolvedConfirm) {
          try {
            const confirmParsed = await analyzeVoiceQuery(rawText);
            resolvedConfirm = inferConfirmationFromNluPayload(confirmParsed);
          } catch {
            // Keep local fallback prompt below if backend classification fails.
          }
        }
        if (voiceSessionRef.current !== sessionId) return;

        if (resolvedConfirm === "yes") {
          const pending = voicePendingIntentRef.current;
          voiceAwaitingConfirmRef.current = false;

          if (!pending) {
            const retryPrompt = pickRandom(
              VOICE_PENDING_RETRY_PROMPTS,
              "I do not have it yet. Please say who you want to meet.",
            );
            setVoiceStage("speaking");
            showVoiceReply(retryPrompt);
            if (voiceSessionRef.current !== sessionId) return;
            startVoiceListening(sessionId);
            return;
          }

          setSearchQuery(pending.normalizedQuery);
          setSearchType(pending.inferredType);

          const searchingPrompt = pickRandom(
            VOICE_SEARCHING_CONFIRMATIONS,
            "Great. Searching now.",
          );
          showVoiceReply(searchingPrompt);
          setVoiceStage("searching");
          handleSearchWithRecent(pending.normalizedQuery);
          return;
        }

        if (resolvedConfirm === "no") {
          voiceAwaitingConfirmRef.current = false;
          voicePendingIntentRef.current = null;
          const retryPrompt = pickRandom(
            VOICE_RETRY_AFTER_NO_PROMPTS,
            "No problem. Say it again and I will confirm before searching.",
          );
          setVoiceStage("speaking");
          showVoiceReply(retryPrompt);
          if (voiceSessionRef.current !== sessionId) return;
          startVoiceListening(sessionId);
          return;
        }

        const yesNoPrompt = pickRandom(
          VOICE_CONFIRM_HINT_PROMPTS,
          "Say yes to search, or no to change it.",
        );
        setVoiceStage("speaking");
        showVoiceReply(yesNoPrompt);
        if (voiceSessionRef.current !== sessionId) return;
        startVoiceListening(sessionId);
        return;
      }

      setVoiceStage("processing");
      setVoiceError("");

      try {
        const parsed = await analyzeVoiceQuery(rawText);
        if (voiceSessionRef.current !== sessionId) return;

        const normalizedQuery =
          String(parsed?.normalizedQuery || "").trim() || rawText;
        const inferredType =
          String(parsed?.searchType || "").trim() ||
          detectSearchType(normalizedQuery);
        const summary =
          String(parsed?.summary || "").trim() ||
          "Got it. I am finding the best matches now.";
        const needsClarification = Boolean(parsed?.needsClarification);
        const confidence = String(parsed?.confidence || "").toLowerCase();
        const isConfident = confidence === "high";
        const isLowConfidence = confidence === "low";
        const clarificationPrompt = String(parsed?.clarificationPrompt || "")
          .trim()
          .slice(0, 180);

        if (needsClarification) {
          voiceAwaitingConfirmRef.current = false;
          voicePendingIntentRef.current = null;
          const followUpPrompt =
            clarificationPrompt ||
            "Who do you want to meet? You can share a name and company, or a role and location.";

          setVoiceStage("speaking");
          setVoiceError("");
          showVoiceReply(followUpPrompt);
          if (voiceSessionRef.current !== sessionId) return;
          startVoiceListening(sessionId);
          return;
        }

        // First low-confidence pass: ask user to repeat once (slowly, in
        // English) before we continue to confirm/search.
        if (isLowConfidence && voiceLowConfidenceRetryRef.current < 1) {
          voiceLowConfidenceRetryRef.current += 1;
          voiceAwaitingConfirmRef.current = false;
          voicePendingIntentRef.current = null;
          const retryPrompt =
            "I may have misheard that. Please repeat slowly, and include a name and company, or a role and location.";
          setVoiceStage("speaking");
          setVoiceError("");
          showVoiceReply(retryPrompt);
          if (voiceSessionRef.current !== sessionId) return;
          startVoiceListening(sessionId);
          return;
        }

        voiceLowConfidenceRetryRef.current = 0;

        // Confident, unambiguous request: skip confirmation and search immediately.
        if (isConfident) {
          voiceAwaitingConfirmRef.current = false;
          voicePendingIntentRef.current = null;

          setSearchQuery(normalizedQuery);
          setSearchType(inferredType);

          const recap = buildVoiceRecap(normalizedQuery, inferredType, summary);
          showVoiceReply(recap);
          setVoiceStage("searching");
          handleSearchWithRecent(normalizedQuery);
          return;
        }

        voicePendingIntentRef.current = {
          normalizedQuery,
          inferredType,
          summary,
        };
        voiceAwaitingConfirmRef.current = true;

        const confirmPrompt = buildConfirmPrompt(
          summary,
          normalizedQuery,
          inferredType,
        );
        setVoiceStage("speaking");
        showVoiceReply(confirmPrompt);
        if (voiceSessionRef.current !== sessionId) return;
        startVoiceListening(sessionId);
      } catch {
        if (voiceSessionRef.current !== sessionId) return;

        voiceAwaitingConfirmRef.current = false;
        voicePendingIntentRef.current = null;
        const retryPrompt =
          "I could not understand that clearly. Please say the name and company, or role and location.";
        setVoiceStage("speaking");
        showVoiceReply(retryPrompt);
        if (voiceSessionRef.current !== sessionId) return;
        startVoiceListening(sessionId);
      }
    },
    [
      analyzeVoiceQuery,
      handleSearchWithRecent,
      setSearchQuery,
      setSearchType,
      showVoiceReply,
      startVoiceListening,
      triggerInstantSearchReturn,
    ],
  );

  const cancelVoiceFlow = useCallback(() => {
    voiceSessionRef.current += 1;
    voiceTranscriptRef.current = "";
    voiceHasStartedRef.current = false;
    voiceLowConfidenceRetryRef.current = 0;
    voiceAwaitingConfirmRef.current = false;
    voicePendingIntentRef.current = null;

    voiceStageRef.current = "idle";
    voiceUnderstandingAbortRef.current?.abort();
    voiceUnderstandingAbortRef.current = null;
    cancelVoiceSession();
    clearPendingVoiceUserLine();
    stopVoiceMeter();

    if (voiceRelistenTimerRef.current) {
      clearTimeout(voiceRelistenTimerRef.current);
      voiceRelistenTimerRef.current = null;
    }
    if (voiceListenHealthTimerRef.current) {
      clearTimeout(voiceListenHealthTimerRef.current);
      voiceListenHealthTimerRef.current = null;
    }
    if (voiceTypewriterTimerRef.current) {
      clearInterval(voiceTypewriterTimerRef.current);
      voiceTypewriterTimerRef.current = null;
    }
    if (voiceInstantTimerRef.current) {
      clearTimeout(voiceInstantTimerRef.current);
      voiceInstantTimerRef.current = null;
    }

    setVoiceError("");
    setVoiceStage("idle");
    setVoiceInstantSwitch(false);
    setVoiceSecondsLeft(VOICE_MAX_DURATION_SECONDS);
    setVoiceTypedLineId(null);
    setVoiceTypedText("");
    setVoiceStatusIndex(0);
    voiceChatLastSignatureRef.current = "";
  }, [
    cancelVoiceSession,
    clearPendingVoiceUserLine,
    stopVoiceCapture,
    stopVoiceMeter,
  ]);

  const handleVoiceAssistantToggle = useCallback(() => {
    if (["processing", "speaking", "searching"].includes(voiceStage)) {
      cancelVoiceFlow();
      return;
    }

    if (!speechSupported) {
      startVoiceCapture(handleVoiceSearchStart);
      return;
    }

    if (isListening) {
      cancelVoiceFlow();
      return;
    }

    if (voiceRelistenTimerRef.current) {
      clearTimeout(voiceRelistenTimerRef.current);
      voiceRelistenTimerRef.current = null;
    }
    if (voiceListenHealthTimerRef.current) {
      clearTimeout(voiceListenHealthTimerRef.current);
      voiceListenHealthTimerRef.current = null;
    }
    voiceSessionRef.current += 1;
    voiceTranscriptRef.current = "";
    voiceHasStartedRef.current = false;
    voiceLowConfidenceRetryRef.current = 0;
    voiceAwaitingConfirmRef.current = false;
    voicePendingIntentRef.current = null;
    voicePendingUserLineIdRef.current = null;
    setVoiceError("");
    setVoiceChatLines([]);
    setVoiceTypedLineId(null);
    setVoiceTypedText("");
    voiceChatLastSignatureRef.current = "";
    setVoiceInstantSwitch(false);
    if (voiceTypewriterTimerRef.current) {
      clearInterval(voiceTypewriterTimerRef.current);
      voiceTypewriterTimerRef.current = null;
    }
    if (voiceInstantTimerRef.current) {
      clearTimeout(voiceInstantTimerRef.current);
      voiceInstantTimerRef.current = null;
    }
    setVoiceStatusIndex(0);
    setVoiceSecondsLeft(VOICE_MAX_DURATION_SECONDS);
    setVoiceStage("processing");
    const sessionId = voiceSessionRef.current;
    startVoiceListening(sessionId);
  }, [
    handleVoiceSearchStart,
    isListening,
    cancelVoiceFlow,
    speechSupported,
    startVoiceCapture,
    startVoiceListening,
    stopVoiceCapture,
    clearPendingVoiceUserLine,
    voiceStage,
  ]);

  useEffect(() => {
    if (voiceStage !== "listening") {
      if (voiceListenHealthTimerRef.current) {
        clearTimeout(voiceListenHealthTimerRef.current);
        voiceListenHealthTimerRef.current = null;
      }
      voiceListenHealthRetryRef.current = 0;
      return;
    }

    if (isListening) {
      if (voiceListenHealthTimerRef.current) {
        clearTimeout(voiceListenHealthTimerRef.current);
        voiceListenHealthTimerRef.current = null;
      }
      return;
    }

    if (isTranscribing) return;

    if (voiceListenHealthTimerRef.current) {
      clearTimeout(voiceListenHealthTimerRef.current);
      voiceListenHealthTimerRef.current = null;
    }

    voiceListenHealthTimerRef.current = setTimeout(() => {
      const timerSessionId = voiceSessionRef.current;
      if (voiceStageRef.current !== "listening") return;
      if (isListening) return;

      if (voiceListenHealthRetryRef.current < 1) {
        voiceListenHealthRetryRef.current += 1;
        setVoiceSecondsLeft(VOICE_MAX_DURATION_SECONDS);
        startVoiceListening(timerSessionId);
        return;
      }

      if (voiceAwaitingConfirmRef.current) {
        voiceListenHealthRetryRef.current = 0;
        startVoiceListening(timerSessionId);
        return;
      }

      if (voiceSessionRef.current !== timerSessionId) return;
      triggerInstantSearchReturn();
    }, 900);

    return () => {
      if (voiceListenHealthTimerRef.current) {
        clearTimeout(voiceListenHealthTimerRef.current);
        voiceListenHealthTimerRef.current = null;
      }
    };
  }, [
    isListening,
    isTranscribing,
    startVoiceListening,
    triggerInstantSearchReturn,
    voiceStage,
  ]);

  useEffect(() => {
    if (!isListening || voiceStage !== "listening") {
      stopVoiceMeter();
      return;
    }

    if (
      typeof window === "undefined" ||
      !navigator?.mediaDevices?.getUserMedia
    ) {
      return;
    }

    let cancelled = false;

    const startVoiceMeter = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const AudioContextClass =
          window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const context = new AudioContextClass();
        if (context.state === "suspended") {
          await context.resume().catch(() => {});
        }

        const source = context.createMediaStreamSource(stream);
        const analyser = context.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.68;
        source.connect(analyser);

        const data = new Uint8Array(analyser.frequencyBinCount);
        const barCount = 4;

        voiceMeterStreamRef.current = stream;
        voiceMeterContextRef.current = context;
        voiceMeterSourceRef.current = source;
        voiceMeterAnalyserRef.current = analyser;

        const tick = () => {
          if (cancelled || !voiceMeterAnalyserRef.current) return;

          analyser.getByteFrequencyData(data);
          const chunkSize = Math.max(1, Math.floor(data.length / barCount));

          const next = Array.from({ length: barCount }, (_, index) => {
            const start = index * chunkSize;
            const end =
              index === barCount - 1
                ? data.length
                : Math.min(data.length, start + chunkSize);

            let sum = 0;
            for (let i = start; i < end; i += 1) {
              sum += data[i];
            }

            const avg = end > start ? sum / (end - start) / 255 : 0;
            return Math.max(0.08, Math.min(1, avg * 2.5));
          });

          setVoiceLevels((prev) =>
            prev.map((value, index) => value * 0.32 + next[index] * 0.68),
          );

          voiceMeterTickRef.current = setTimeout(tick, 75);
        };

        voiceMeterTickRef.current = setTimeout(tick, 0);
      } catch {
        setVoiceLevels([0.11, 0.14, 0.13, 0.1]);
      }
    };

    startVoiceMeter();

    return () => {
      cancelled = true;
      stopVoiceMeter();
    };
  }, [isListening, stopVoiceMeter, voiceStage]);

  useEffect(() => {
    if (
      voiceStage !== "processing" &&
      voiceStage !== "searching" &&
      voiceStage !== "speaking"
    ) {
      return;
    }

    const statusTimer = setInterval(() => {
      setVoiceStatusIndex(
        (current) => (current + 1) % VOICE_LOADING_STATUS.length,
      );
    }, 1500);

    return () => clearInterval(statusTimer);
  }, [voiceStage]);

  useEffect(() => {
    if (voiceStage !== "searching") return;
    if (searchError) {
      setVoiceError(searchError);
      setVoiceStage("error");
      return;
    }
    if (showResults) {
      const timerSessionId = voiceSessionRef.current;
      setVoiceStage("done");
      const doneTimer = setTimeout(() => {
        if (voiceSessionRef.current !== timerSessionId) return;
        setVoiceStage("idle");
      }, 600);
      return () => clearTimeout(doneTimer);
    }
  }, [searchError, showResults, voiceStage]);

  useEffect(() => {
    if (voiceStage !== "searching") return;
    if (searchError || showResults || isLoading || showSkeleton) return;

    const sessionId = voiceSessionRef.current;
    const fallbackTimer = setTimeout(() => {
      if (voiceSessionRef.current !== sessionId) return;
      if (voiceStageRef.current !== "searching") return;
      setVoiceStage("idle");
    }, 1400);

    return () => clearTimeout(fallbackTimer);
  }, [isLoading, searchError, showResults, showSkeleton, voiceStage]);

  useEffect(() => {
    if (isListening) {
      voiceHasStartedRef.current = true;
      setVoiceStage("listening");
    }
  }, [isListening]);

  useEffect(() => {
    const shouldShowPendingLine =
      voiceStage === "listening" && (isListening || isTranscribing);

    if (shouldShowPendingLine) {
      ensureVoiceUserListeningLine();
      return;
    }

    clearPendingVoiceUserLine();
  }, [
    clearPendingVoiceUserLine,
    ensureVoiceUserListeningLine,
    isListening,
    isTranscribing,
    voiceStage,
  ]);

  useEffect(() => {
    if (voiceStage !== "listening") return;
    if (isListening || isTranscribing) return;
    if (!voiceHasStartedRef.current) return;

    if (!voiceTranscriptRef.current.trim()) {
      if (voiceAwaitingConfirmRef.current) {
        startVoiceListening(voiceSessionRef.current);
        return;
      }
      triggerInstantSearchReturn();
    }
  }, [
    isListening,
    isTranscribing,
    startVoiceListening,
    triggerInstantSearchReturn,
    voiceStage,
  ]);

  useEffect(() => {
    return () => {
      voiceUnderstandingAbortRef.current?.abort();
      stopVoiceMeter();
      if (voiceRelistenTimerRef.current) {
        clearTimeout(voiceRelistenTimerRef.current);
        voiceRelistenTimerRef.current = null;
      }
      if (voiceListenHealthTimerRef.current) {
        clearTimeout(voiceListenHealthTimerRef.current);
        voiceListenHealthTimerRef.current = null;
      }
      if (voiceInstantTimerRef.current) {
        clearTimeout(voiceInstantTimerRef.current);
        voiceInstantTimerRef.current = null;
      }
    };
  }, [stopVoiceMeter]);

  // Enter must honour the exact same rules as the submit arrow button.
  const isSubmitBlocked = () => isLoading || !hasStarted || !isSearchValid();

  const handleKeyDown = (e) => {
    // Keyboard navigation in results
    if (showResults && results.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveCardIndex((prev) => Math.min(prev + 1, results.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveCardIndex((prev) => Math.max(prev - 1, 0));
        return;
      }
      if (
        e.key === "Enter" &&
        activeCardIndex >= 0 &&
        cardProfileIds[activeCardIndex]
      ) {
        e.preventDefault();
        router.push(`/profile/${cardProfileIds[activeCardIndex]}`);
        return;
      }
    }
    if (e.key === "Enter") {
      if (isSubmitBlocked()) {
        e.preventDefault();
        return;
      }
      handleSearchWithRecent();
    }
  };

  const handleViewProfile = (user) => {
    const id = user.url || user.id;
    if (id) router.push(`/profile/${id}`);
  };

  useEffect(() => {
    setupVoiceRecognition(
      (transcript) => {
        if (
          voiceStageRef.current !== "listening" &&
          !voiceAwaitingConfirmRef.current
        ) {
          return;
        }
        const cleanedTranscript = String(transcript || "").trim();
        if (!cleanedTranscript) return;
        const normalizedTranscript = cleanedTranscript
          .toLowerCase()
          .replace(/[^\p{L}\p{N}]+/gu, " ")
          .trim();

        if (
          normalizedTranscript &&
          normalizedTranscript === voiceLastAssistantReplyRef.current
        ) {
          stopVoiceCapture({ suppressPendingResult: true });
          startVoiceListening(voiceSessionRef.current);
          return;
        }

        // End listening UI immediately once we have user text to avoid
        // lingering "Listening..." state and late empty placeholder rows.
        stopVoiceCapture({ suppressPendingResult: true });

        voiceTranscriptRef.current = cleanedTranscript;
        commitVoiceUserLine(cleanedTranscript);

        processVoiceTranscript(cleanedTranscript, voiceSessionRef.current);
      },
      (partialTranscript) => {
        // Stream the live transcript into the "You:" placeholder as the user
        // speaks, so the line fills in real time instead of after they stop.
        const pendingId = voicePendingUserLineIdRef.current;
        if (!pendingId) return;
        const cleanPartial = String(partialTranscript || "").trim();
        if (!cleanPartial) return;
        voiceLiveTranscriptRef.current = cleanPartial;
        setVoiceTypedLineId(pendingId);
        setVoiceTypedText(cleanPartial);
      },
    );
  }, [
    commitVoiceUserLine,
    processVoiceTranscript,
    setupVoiceRecognition,
    startVoiceListening,
    stopVoiceCapture,
  ]);

  const hasInvalidNameCharacters = (name) => {
    const NAME_PATTERN = /^[\p{L}\s'-]*$/u;
    return name.trim() && !NAME_PATTERN.test(name.trim());
  };

  const getValidationError = () => {
    if (searchError) return searchError;

    if (!searchQuery.trim()) {
      return null;
    }

    const type = detectSearchType(searchQuery);

    switch (type) {
      case "name":
        if (hasInvalidNameCharacters(searchQuery)) {
          return VALIDATION_MESSAGES.name;
        }
        break;
      case "email":
        if (searchQuery.trim() && !isValidEmail(searchQuery)) {
          return VALIDATION_MESSAGES.email;
        }
        break;
      case "linkedin":
        if (searchQuery.trim() && !isValidLinkedIn(searchQuery)) {
          return VALIDATION_MESSAGES.linkedin;
        }
        break;
    }

    return null;
  };

  const getSmartPlaceholder = () => {
    const type = detectSearchType(searchQuery);
    const suggestion = getSmartSuggestion(searchQuery, type);

    switch (type) {
      case "email":
        if (searchQuery && !hookIsValidEmail(searchQuery)) {
          return "Please enter a complete email address...";
        }
        return suggestion
          ? `Try: ${suggestion}`
          : "Enter complete email address...";
      case "linkedin":
        const cleanInput = searchQuery.toLowerCase().trim();

        if (cleanInput.startsWith("htt") && cleanInput.length < 8) {
          return "https://linkedin.com/in/username";
        }

        if (cleanInput.startsWith("link") && cleanInput.length < 10) {
          return "linkedin.com/in/username";
        }

        if (cleanInput.startsWith("www") && cleanInput.length < 10) {
          return "www.linkedin.com/in/username";
        }

        if (cleanInput.startsWith("/") && cleanInput.length < 5) {
          return "/in/username";
        }

        if (searchQuery && !hookIsValidLinkedIn(searchQuery)) {
          return "Complete the LinkedIn profile URL...";
        }

        return suggestion
          ? `Try: ${suggestion}`
          : "Enter public profile URL...";
      case "discover":
        return 'e.g. "Java developers" or "VP Marketing at a SaaS company in NYC"…';
      default:
        const trimmedQuery = searchQuery.trim();
        const words = trimmedQuery
          .split(/\s+/)
          .filter((word) => word.length > 0);

        if (
          trimmedQuery.endsWith(" ") &&
          words.length === 1 &&
          words[0].length >= 2
        ) {
          return "Now enter your family name...";
        }

        if (
          words.length === 1 &&
          words[0].length >= 2 &&
          !trimmedQuery.endsWith(" ")
        ) {
          return "Add a space and enter family name...";
        }

        if (searchQuery && words.length < 2) {
          return "Enter first and last name...";
        }
    }
  };

  const getSmartIcon = () => {
    const type = detectSearchType(searchQuery);
    switch (type) {
      case "email":
        return (
          <EmailIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
        );
      case "linkedin":
        return (
          <LinkedInIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
        );
      case "discover":
        return (
          <TravelExploreIcon
            sx={{ color: theme.palette.text.secondary, mr: 1 }}
          />
        );
      default:
        return (
          <PersonIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
        );
    }
  };

  const getSubtitle = () => {
    const validationError = getValidationError();
    if (validationError) {
      return validationError;
    }

    if (hasStarted && searchQuery) {
      const trimmedQuery = searchQuery.trim();
      const words = trimmedQuery.split(/\s+/).filter((word) => word.length > 0);
      const type = detectSearchType(searchQuery);

      if (type === "name") {
        if (searchQuery.length >= 2) {
          if (
            trimmedQuery.endsWith(" ") &&
            words.length === 1 &&
            words[0].length >= 2
          ) {
            return "Now enter your family name...";
          }

          if (
            words.length === 1 &&
            words[0].length >= 2 &&
            !trimmedQuery.endsWith(" ")
          ) {
            return "Add a space and enter family name...";
          }

          if (words.length >= 2) {
            return "In the next step, add their workplace.";
          }
        }

        if (searchQuery.length > 0 && searchQuery.length < 2) {
          return "Keep typing your first name...";
        }
      }

      if (type === "email") {
        if (hookIsValidEmail(searchQuery)) {
          return "Email looks good! Ready to search.";
        }
        return "Complete your email address to search.";
      }

      if (type === "discover") {
        return "We'll find the best matches for you.";
      }

      if (type === "linkedin") {
        const cleanInput = searchQuery.toLowerCase().trim();

        if (cleanInput.startsWith("htt") && cleanInput.length < 8) {
          return "Keep typing the full public profile URL...";
        }

        if (cleanInput.startsWith("link") && cleanInput.length < 10) {
          return "Continue with social.com/username...";
        }

        if (cleanInput.startsWith("www") && cleanInput.length < 10) {
          return "Continue with social.com/username...";
        }

        if (cleanInput.startsWith("/") && cleanInput.length < 5) {
          return "Add 'in/username' after the slash...";
        }

        if (hookIsValidLinkedIn(searchQuery)) {
          return "Public profile URL is valid! Ready to search.";
        }

        if (cleanInput.includes("linkedin") && !cleanInput.includes("/in/")) {
          return "Add '/in/username' to complete the public profile URL...";
        }

        return "Enter full social profile URL (e.g. social.com/name)";
      }
    }

    return "Search with any details you know.";
  };

  if (showSkeleton) {
    const hasSinglePersonMatch = isNameOnlySearch && rcTotalCount === 1;
    if (skeletonMode === "table" && !hasSinglePersonMatch) {
      return (
        <CandidateListSkeleton
          complete={tableSkeletonReady}
          query={searchQuery}
        />
      );
    }
    return <ProfileCardSkeleton onNewSearch={resetSearch} />;
  }

  if (showResults) {
    return (
      <SearchResults
        results={results}
        moreResults={moreResults}
        rawCandidates={rawCandidates}
        showTableNarrowHint={showTableNarrowHint}
        rcTotalCount={rcTotalCount}
        rcHasMore={rcHasMore}
        externalLimitReached={externalLimitReached}
        savedHasMore={savedHasMore}
        isLoadingMoreRC={isLoadingMoreRC}
        enrichingId={enrichingId}
        enrichedUrls={enrichedUrls}
        onShowMore={handleShowMore}
        onLoadNextRCPage={handleLoadNextRCPage}
        onEnrichOne={handleEnrichOne}
        onNewSearch={resetSearch}
        onRefineSearch={() => {
          setVoiceStage("idle");
          refineSearch();
        }}
        onViewProfile={handleViewProfile}
        isNameOnly={isNameOnlySearch}
        suggestions={suggestions}
        suggestionLabel={suggestionLabel}
        searchType={searchType}
        searchQuery={searchQuery}
        nameQuery={nameQuery}
        companyQuery={companyQuery}
        externalSearchUnavailable={externalSearchUnavailable}
        searchCooldown={searchCooldown}
        retryInitialSearch={retryInitialSearch}
        onRetrySearch={() => handleSearch(searchQuery)}
        onBroadenSearch={(broadenSearch) =>
          handleSearch(searchQuery, { broadenSearch })
        }
        activeCardIndex={activeCardIndex}
        onCardFocus={setActiveCardIndex}
        isGuestUser={isGuestUser}
        userEmail={session?.user?.email || ""}
      />
    );
  }

  const isDisabled = isSubmitBlocked();

  const hasValidationError = !!getValidationError();

  return (
    <FullScreenContainer data-page="search">
      <Fade in={showContent} timeout={300}>
        <ContentWrapper
          sx={{
            transform: isGuestUser
              ? { xs: "translateY(-40px)", md: "translateY(-78px)" }
              : "none",
            transition: "transform 0.2s ease",
          }}
        >
          {/* Title Section */}
          <Box
            sx={{
              textAlign: "center",
              width: "100%",
              position: "relative",
              zIndex: 3,
            }}
          >
            <Typography
              variant={isMobile ? "h4b" : "h2b"}
              component="h1"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "24px", sm: "28px", md: "36px" },
                fontWeight: 300,
                color: theme.palette.text.primary,
                mb: { xs: 0, md: 2 },
              }}
            >
              Who do you want to meet?
            </Typography>
          </Box>

          {/* Search Section */}
          {/* Search input wrapper — position:relative so dropdowns anchor here */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: isVoiceModeActive ? { xs: 3.2, md: 4.5 } : 0,
              position: "relative",
              zIndex: 1,
            }}
          >
            <SearchHalo sx={{ px: { xs: 2, md: 0 } }}>
              <SearchModeLayer
                active={!isVoiceModeActive}
                instant={voiceInstantSwitch}
              >
                <SearchContainer
                  expanded={hasStarted}
                  currentStep={0}
                  searchData={{ query: searchQuery }}
                  fieldErrors={{}}
                  placeholder={getSmartPlaceholder()}
                  startAdornment={getSmartIcon()}
                  isValid={isSearchValid()}
                  isMobile={isMobile}
                  inputRef={inputRef}
                  value={searchQuery}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onButtonClick={handleSearchWithRecent}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setTimeout(() => setInputFocused(false), 150)}
                  disabled={isDisabled}
                  isLoading={isLoading}
                  isListening={isListening}
                  speechSupported={speechSupported}
                  onVoiceSearch={handleVoiceAssistantToggle}
                />
              </SearchModeLayer>

              <VoiceSessionPanel
                active={isVoiceModeActive}
                instant={voiceInstantSwitch}
              >
                <VoiceBlobRow aria-label="Voice activity">
                  {voiceLevels.map((level, index) => (
                    <VoiceBlob
                      key={`voice-blob-${index}`}
                      stage={voiceStage}
                      level={level}
                      isListening={voiceStage === "listening"}
                      isSpeaking={voiceStage === "listening" && level > 0.12}
                    />
                  ))}
                </VoiceBlobRow>

                <Box
                  sx={{
                    width: "100%",
                    px: { xs: 0.5, sm: 2.5 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 132,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 560,
                      mx: "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    {isVoiceModeActive && (
                      <IconButton
                        aria-label="End voice"
                        title="End voice"
                        size="small"
                        onClick={cancelVoiceFlow}
                        sx={{
                          color: "text.secondary",
                          p: 0.5,
                          "&:hover": { backgroundColor: "transparent" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.text.primary, 0.6),
                        lineHeight: 1.35,
                        fontSize: { xs: "0.78rem", sm: "0.84rem" },
                        whiteSpace: "nowrap",
                        overflow: "visible",
                        textOverflow: "clip",
                        textAlign: "center",
                        flex: 0,
                        minWidth: "max-content",
                      }}
                    >
                      {voiceStatusLabel}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 560,
                      mt: 2.4,
                      height: 84,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.35,
                      justifyContent: "flex-start",
                    }}
                    aria-live="polite"
                  >
                    {voiceChatLines.map((line) => {
                      const isTyping = line.id === voiceTypedLineId;
                      const content = isTyping ? voiceTypedText : line.text;

                      if (!isTyping && !String(content || "").trim()) {
                        return null;
                      }

                      return (
                        <Typography
                          key={`voice-chat-line-${line.id}`}
                          variant="caption"
                          sx={{
                            color: alpha(theme.palette.text.primary, 0.77),
                            fontSize: { xs: "0.72rem", sm: "0.78rem" },
                            lineHeight: 1.35,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textAlign: "left",
                            minHeight: "1.35em",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              color: alpha(theme.palette.text.primary, 0.45),
                              fontSize: "0.62rem",
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              mr: 0.55,
                              flexShrink: 0,
                            }}
                          >
                            {line.speaker === "user" ? "You" : "Meex AI"}:
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              minWidth: 0,
                            }}
                          >
                            {content}
                          </Box>
                          {isTyping && (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                flexShrink: 0,
                                width: 6,
                                height: "0.88em",
                                ml: 0.4,
                                borderRadius: "1px",
                                background: alpha(
                                  theme.palette.text.primary,
                                  0.65,
                                ),
                                verticalAlign: "-0.08em",
                                animation: `${voiceTypewriterCaret} 900ms steps(1) infinite`,
                              }}
                            />
                          )}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>
              </VoiceSessionPanel>
            </SearchHalo>

            {!isVoiceModeActive && voiceChatLines.length > 0 && (
              <Box
                aria-label="Voice conversation"
                sx={{
                  width: "100%",
                  maxWidth: 560,
                  px: 2,
                  mt: 2,
                }}
              >
                {voiceChatLines.map((line) => (
                  <Typography
                    key={`voice-history-${line.id}`}
                    variant="caption"
                    component="p"
                    sx={{ color: "text.secondary", mb: 0.5 }}
                  >
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {line.speaker === "user" ? "You" : "Meex AI"}:
                    </Box>{" "}
                    {line.text}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Recent searches — shown when input is focused and empty (first step) */}
            {!isMobile &&
              !isVoiceModeActive &&
              inputFocused &&
              !searchQuery &&
              recentSearches.length > 0 && (
                <SearchDropdown
                  mode="recent"
                  items={recentSearches}
                  onSelect={(q) => {
                    setSearchQuery(q);
                    setSearchType(detectSearchType(q));
                    setInputFocused(false);
                    if (inputRef.current) inputRef.current.value = q;
                  }}
                  onRemove={removeRecent}
                  onClearAll={clearRecent}
                />
              )}

            {/* Subtitle */}
            {!isVoiceModeActive && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  color: hasValidationError
                    ? theme.palette.error.main
                    : theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : "rgba(74, 85, 108, 0.95)",
                  lineHeight: 1.5,
                  opacity: hasValidationError ? 1 : { xs: 0.92, md: 0.72 },
                  mt: { xs: 1, md: 1 },
                  mb: { xs: 2, md: 1 },
                  pl: { xs: 3, md: 2 },
                  pr: { xs: 2, md: 0 },
                  ml: 0,
                  textAlign: "left",
                  width: "100%",
                  maxWidth: "700px",
                  fontSize: { xs: "0.8rem", md: "0.9rem" },
                }}
              >
                {getSubtitle()}
              </Typography>
            )}

            {(voiceSessionError || voiceError) && (
              <Typography
                variant="caption"
                sx={{
                  color: "error.main",
                  mt: -1,
                  mb: 1,
                  width: "100%",
                  maxWidth: "700px",
                  px: { xs: 2, md: 0 },
                  textAlign: "left",
                  ml: 0,
                }}
              >
                {voiceSessionError || voiceError}
              </Typography>
            )}

            <Box
              sx={{
                width: "100%",
                minHeight: 24,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                mt: 1,
              }}
            >
              {isLoading &&
                !showResults &&
                !showSkeleton &&
                !isVoiceModeActive && (
                  <SearchWaveLoader aria-label="Loading search results">
                    <span />
                    <span />
                    <span />
                  </SearchWaveLoader>
                )}
            </Box>
          </Box>
        </ContentWrapper>
      </Fade>
    </FullScreenContainer>
  );
}
